import { getToken } from "~/storages/token";
import { JsonResponseType } from "~/types/jsonResponseType";

type HeaderType = Record<string, string> | undefined;

interface BaseApiCreationProps {
  baseUrl: string;
}

interface BaseApiProps<TResponse, TMapperInput> {
  method: "POST" | "GET" | "PUT" | "DELETE";
  responseType?: "json" | "blob" | "text";
  action: string;
  body?: BodyInit | null | undefined;
  groupSid?: string;
  headers?: HeaderType;
  params?: URLSearchParams;
  responseMapper?: (input: TMapperInput) => JsonResponseType<TResponse>;
}

export const BaseApi = ({ baseUrl }: BaseApiCreationProps) => {
  return async <TResponse, TInput = undefined>({
    method = "POST",
    action,
    body,
    groupSid,
    headers = {},
    responseType = "json",
    params,
    responseMapper,
  }: BaseApiProps<TResponse, TInput>) => {
    const token = getToken();

    const requestHeaders: Record<string, string> = {
      Accept:
        responseType === "json"
          ? "application/json"
          : responseType === "blob"
            ? "application/octet-stream"
            : responseType === "text"
              ? "text/plain"
              : "application/json",
      ...headers,
    };

    if (!(body instanceof FormData)) {
      requestHeaders["Content-Type"] = "application/json";
    }

    if (token) requestHeaders["Authorization"] = `Bearer ${token}`;
    if (groupSid) requestHeaders["GroupSid"] = groupSid;

    try {
      const res = await fetch(
        `${baseUrl}/${action}${params ? `?${params}` : ""}`,
        {
          method,
          body,
          headers: requestHeaders,
        },
      );

      if (!res.ok) {
        if (res.status === 400) {
          throw "BadRequest";
        } else if (res.status === 401) {
          throw "Unauthorized";
        } else if (res.status === 403) {
          throw "Forbidden";
        } else if (res.status === 404) {
          throw "NotFound";
        } else if (res.status === 408) {
          throw "RequestTimeout";
        } else if (res.status === 429) {
          throw "TooManyRequests";
        } else if (res.status === 500) {
          throw "InternalServerError";
        } else if (res.status === 502) {
          throw "BadGateway";
        } else if (res.status === 503) {
          throw "ServiceUnavailable";
        } else if (res.status === 504) {
          throw "GatewayTimeout";
        } else {
          throw `UnexpectedError:${res.status}`;
        }
      }

      if (res.status === 204) {
        return undefined as TResponse;
      } else if (responseType === "json") {
        const rawJson = await res.json();
        const json = (
          responseMapper ? responseMapper(rawJson) : rawJson
        ) as JsonResponseType<TResponse>;

        if (json.succeed) {
          return json.data ? (json.data as TResponse) : (json as TResponse);
        } else {
          throw json.messageCode || "UnexpectedError";
        }
      } else if (responseType === "blob") {
        return (await res.blob()) as TResponse;
      } else if (responseType === "text") {
        return (await res.text()) as TResponse;
      } else {
        return res as TResponse;
      }
    } catch (error) {
      throw new Error(error as string);
    }
  };
};
